import React, { useState, useEffect } from 'react'
import { useTheme } from '../context/ThemeContext'
import { formatCurrency } from '../utils/currency'
import { formatDate } from '../utils/dateFormat'
import { financialDashboardService } from '../services/financialDashboard'

export default function FinancialStatements() {
  const { isDark } = useTheme()
  const [activeTab, setActiveTab] = useState('summary')
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0])
  const [financialData, setFinancialData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch real financial data from API
  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        setLoading(true)

        const result = await financialDashboardService.getFinancialDashboardData({ asOfDate })

        if (result.success) {
          setFinancialData(result.data)
          setError(null)
        } else {
          setError('Failed to load financial data')
        }
      } catch (err) {
        console.error('Error fetching financial data:', err)
        setError('Error connecting to server')
      } finally {
        setLoading(false)
      }
    }

    fetchFinancialData()
  }, [asOfDate])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading financial data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-red-600">
          <p className="font-semibold">Error Loading Data</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  if (!financialData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-600">
          <p>No financial data available</p>
        </div>
      </div>
    )
  }

  // Use real financialData from API
  const data = financialData

  const renderReportSummary = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold mb-4">Financial Overview</h3>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Total Revenue</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(financialData.summary.totalRevenue)}</div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Net Profit</div>
          <div className="text-2xl font-bold text-blue-600">{formatCurrency(financialData.summary.netProfit)}</div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Total Assets</div>
          <div className="text-2xl font-bold text-purple-600">{formatCurrency(financialData.summary.totalAssets)}</div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Total Equity</div>
          <div className="text-2xl font-bold text-indigo-600">{formatCurrency(financialData.summary.totalEquity)}</div>
        </div>
      </div>

      {/* Financial Ratios */}
      <h4 className="text-lg font-semibold mt-6 mb-3">Financial Ratios</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Gross Profit Margin</div>
          <div className="text-lg font-semibold">{financialData.summary.grossProfitMargin}%</div>
        </div>
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Net Profit Margin</div>
          <div className="text-lg font-semibold">{financialData.summary.netProfitMargin}%</div>
        </div>
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Return on Assets</div>
          <div className="text-lg font-semibold">{financialData.summary.returnOnAssets}%</div>
        </div>
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Current Ratio</div>
          <div className="text-lg font-semibold">{financialData.summary.currentRatio}</div>
        </div>
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Quick Ratio</div>
          <div className="text-lg font-semibold">{financialData.summary.quickRatio}</div>
        </div>
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
          <div className="text-sm text-gray-500">Cash Ratio</div>
          <div className="text-lg font-semibold">{financialData.summary.cashRatio}</div>
        </div>
      </div>

      {/* Balance Sheet Summary */}
      <h4 className="text-lg font-semibold mt-6 mb-3">Balance Sheet Summary</h4>
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">Total Assets</div>
            <div className="text-xl font-bold text-green-600">{formatCurrency(financialData.summary.totalAssets)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Total Liabilities</div>
            <div className="text-xl font-bold text-red-600">{formatCurrency(financialData.summary.totalLiabilities)}</div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderBalanceSheet = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold mb-4">Balance Sheet</h3>
      <p className="text-sm text-gray-500">As of {formatDate(asOfDate)}</p>

      {/* Assets Section */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Assets</h4>

        <h5 className="font-medium mb-2">Current Assets</h5>
        <div className="space-y-1 ml-4">
          <div className="flex justify-between">
            <span>Bank Accounts</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.assets.current.bankAccounts)}</span>
          </div>
          <div className="flex justify-between">
            <span>Trade Debtors</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.assets.current.tradeDebtors)}</span>
          </div>
          <div className="flex justify-between">
            <span>Inventory</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.assets.current.inventory)}</span>
          </div>
          <div className="flex justify-between">
            <span>Prepaid Expenses</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.assets.current.prepaidExpenses)}</span>
          </div>
          <div className="flex justify-between border-t pt-1 mt-2">
            <span className="font-semibold">Total Current Assets</span>
            <span className="font-bold">{formatCurrency(financialData.balanceSheet.assets.current.totalCurrentAssets)}</span>
          </div>
        </div>

        <h5 className="font-medium mb-2 mt-4">Non-Current Assets</h5>
        <div className="space-y-1 ml-4">
          <div className="flex justify-between">
            <span>Loan to Related Party</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.assets.nonCurrent.loanToRelatedParty)}</span>
          </div>
          <div className="flex justify-between">
            <span>Motor Vehicles</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.assets.nonCurrent.motorVehicles)}</span>
          </div>
          <div className="flex justify-between">
            <span>Fixtures & Fittings</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.assets.nonCurrent.fixturesFittings)}</span>
          </div>
          <div className="flex justify-between">
            <span>Tools & Equipment</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.assets.nonCurrent.toolsEquipment)}</span>
          </div>
          <div className="flex justify-between border-t pt-1 mt-2">
            <span className="font-semibold">Total Non-Current Assets</span>
            <span className="font-bold">{formatCurrency(financialData.balanceSheet.assets.nonCurrent.totalNonCurrentAssets)}</span>
          </div>
        </div>

        <div className="flex justify-between border-t-2 pt-2 mt-4">
          <span className="font-bold text-lg">TOTAL ASSETS</span>
          <span className="font-bold text-lg text-green-600">{formatCurrency(financialData.balanceSheet.assets.totalAssets)}</span>
        </div>
      </div>

      {/* Liabilities Section */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Liabilities</h4>

        <h5 className="font-medium mb-2">Current Liabilities</h5>
        <div className="space-y-1 ml-4">
          <div className="flex justify-between">
            <span>Trade Creditors</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.liabilities.current.tradeCreditors)}</span>
          </div>
          <div className="flex justify-between">
            <span>Loan From Related Party</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.liabilities.current.loanFromRelatedParty)}</span>
          </div>
          <div className="flex justify-between">
            <span>Accrued Expenses</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.liabilities.current.accruedExpenses)}</span>
          </div>
          <div className="flex justify-between">
            <span>Tax Payable</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.liabilities.current.taxPayable)}</span>
          </div>
          <div className="flex justify-between border-t pt-1 mt-2">
            <span className="font-semibold">Total Current Liabilities</span>
            <span className="font-bold">{formatCurrency(financialData.balanceSheet.liabilities.current.totalCurrentLiabilities)}</span>
          </div>
        </div>

        <h5 className="font-medium mb-2 mt-4">Non-Current Liabilities</h5>
        <div className="space-y-1 ml-4">
          <div className="flex justify-between">
            <span>Long-term Loans</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.liabilities.nonCurrent.longTermLoans)}</span>
          </div>
          <div className="flex justify-between">
            <span>Mortgages</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.liabilities.nonCurrent.mortgages)}</span>
          </div>
          <div className="flex justify-between border-t pt-1 mt-2">
            <span className="font-semibold">Total Non-Current Liabilities</span>
            <span className="font-bold">{formatCurrency(financialData.balanceSheet.liabilities.nonCurrent.totalNonCurrentLiabilities)}</span>
          </div>
        </div>

        <div className="flex justify-between border-t-2 pt-2 mt-4">
          <span className="font-bold text-lg">TOTAL LIABILITIES</span>
          <span className="font-bold text-lg text-red-600">{formatCurrency(financialData.balanceSheet.liabilities.totalLiabilities)}</span>
        </div>
      </div>

      {/* Equity Section */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Equity</h4>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span>Capital Fund</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.equity.capitalFund)}</span>
          </div>
          <div className="flex justify-between">
            <span>Owners Drawings</span>
            <span className="font-medium text-red-600">{formatCurrency(financialData.balanceSheet.equity.ownersDrawings)}</span>
          </div>
          <div className="flex justify-between">
            <span>Retained Earnings</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.equity.retainedEarnings)}</span>
          </div>
          <div className="flex justify-between">
            <span>Current Year Earnings</span>
            <span className="font-medium">{formatCurrency(financialData.balanceSheet.equity.currentYearEarnings)}</span>
          </div>
          <div className="flex justify-between border-t-2 pt-2 mt-2">
            <span className="font-bold text-lg">TOTAL EQUITY</span>
            <span className="font-bold text-lg text-blue-600">{formatCurrency(financialData.balanceSheet.equity.totalEquity)}</span>
          </div>
        </div>
      </div>

      {/* Verification */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-green-50'}`}>
        <div className="flex items-center justify-between">
          <span className="font-semibold">Balance Check:</span>
          <span className="text-green-600 font-bold">
            Assets ({formatCurrency(financialData.balanceSheet.assets.totalAssets)}) =
            Liabilities ({formatCurrency(financialData.balanceSheet.liabilities.totalLiabilities)}) +
            Equity ({formatCurrency(financialData.balanceSheet.equity.totalEquity)})
          </span>
        </div>
      </div>
    </div>
  )

  const renderIncomeStatement = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold mb-4">Income Statement</h3>
      <p className="text-sm text-gray-500">For the period ended {formatDate(asOfDate)}</p>

      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        {/* Revenue */}
        <div className="flex justify-between items-center mb-4">
          <span className="font-semibold">Revenue</span>
          <span className="font-bold text-green-600">{formatCurrency(financialData.incomeStatement.revenue.totalRevenue)}</span>
        </div>

        {/* Cost of Sales */}
        <div className="mb-4">
          <div className="font-semibold mb-2">Cost of Sales:</div>
          <div className="space-y-1 ml-4">
            <div className="flex justify-between">
              <span>Materials</span>
              <span>{formatCurrency(financialData.incomeStatement.costOfSales.materials)}</span>
            </div>
            <div className="flex justify-between">
              <span>Subcontractors</span>
              <span>{formatCurrency(financialData.incomeStatement.costOfSales.subcontractors)}</span>
            </div>
            <div className="flex justify-between">
              <span>Transportation</span>
              <span>{formatCurrency(financialData.incomeStatement.costOfSales.transportation)}</span>
            </div>
            <div className="flex justify-between">
              <span>Insurance</span>
              <span>{formatCurrency(financialData.incomeStatement.costOfSales.insurance)}</span>
            </div>
            <div className="flex justify-between border-t pt-1 mt-2">
              <span className="font-semibold">Total Cost of Sales</span>
              <span className="font-semibold text-red-600">({formatCurrency(financialData.incomeStatement.costOfSales.totalCostOfSales)})</span>
            </div>
          </div>
        </div>

        {/* Gross Profit */}
        <div className="flex justify-between border-t-2 pt-2 mb-4">
          <span className="font-bold">Gross Profit</span>
          <div className="text-right">
            <span className="font-bold text-green-600">{formatCurrency(financialData.incomeStatement.grossProfit.amount)}</span>
            <div className="text-sm text-gray-500">({financialData.incomeStatement.grossProfit.margin}% margin)</div>
          </div>
        </div>

        {/* Operating Expenses */}
        <div className="mb-4">
          <div className="font-semibold mb-2">Operating Expenses:</div>
          <div className="space-y-1 ml-4">
            <div className="flex justify-between">
              <span>General & Admin Expenses</span>
              <span>{formatCurrency(financialData.incomeStatement.operatingExpenses.generalAdmin)}</span>
            </div>
            <div className="flex justify-between">
              <span>Operating Expenses</span>
              <span>{formatCurrency(financialData.incomeStatement.operatingExpenses.operating)}</span>
            </div>
            <div className="flex justify-between">
              <span>Employment Expenses</span>
              <span>{formatCurrency(financialData.incomeStatement.operatingExpenses.employment)}</span>
            </div>
            <div className="flex justify-between border-t pt-1 mt-2">
              <span className="font-semibold">Total Operating Expenses</span>
              <span className="font-semibold text-red-600">({formatCurrency(financialData.incomeStatement.operatingExpenses.totalOperatingExpenses)})</span>
            </div>
          </div>
        </div>

        {/* Other Expenses */}
        <div className="mb-4">
          <div className="font-semibold mb-2">Other Expenses:</div>
          <div className="space-y-1 ml-4">
            <div className="flex justify-between">
              <span>Depreciation</span>
              <span>{formatCurrency(financialData.incomeStatement.otherExpenses.depreciation)}</span>
            </div>
            <div className="flex justify-between">
              <span>Interest</span>
              <span>{formatCurrency(financialData.incomeStatement.otherExpenses.interest)}</span>
            </div>
            <div className="flex justify-between border-t pt-1 mt-2">
              <span className="font-semibold">Total Other Expenses</span>
              <span className="font-semibold text-red-600">({formatCurrency(financialData.incomeStatement.otherExpenses.totalOtherExpenses)})</span>
            </div>
          </div>
        </div>

        {/* Net Profit */}
        <div className="flex justify-between border-t-2 pt-2">
          <span className="font-bold text-lg">NET PROFIT</span>
          <div className="text-right">
            <span className="font-bold text-lg text-blue-600">{formatCurrency(financialData.incomeStatement.netProfit.amount)}</span>
            <div className="text-sm text-gray-500">({financialData.incomeStatement.netProfit.margin}% margin)</div>
          </div>
        </div>
      </div>

      {/* Profit Summary */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-blue-50'}`}>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-sm text-gray-500">Gross Profit</div>
            <div className="text-lg font-bold text-green-600">{formatCurrency(financialData.incomeStatement.grossProfit.amount)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Total Expenses</div>
            <div className="text-lg font-bold text-red-600">{formatCurrency(financialData.incomeStatement.operatingExpenses.totalOperatingExpenses + financialData.incomeStatement.otherExpenses.totalOtherExpenses)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Net Profit</div>
            <div className="text-lg font-bold text-blue-600">{formatCurrency(financialData.incomeStatement.netProfit.amount)}</div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderCashFlow = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold mb-4">Cash Flow Statement</h3>
      <p className="text-sm text-gray-500">For the period ended {formatDate(asOfDate)}</p>

      {/* Operating Activities */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Operating Activities</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Cash from Customers</span>
            <span className="font-medium text-green-600">{formatCurrency(financialData.cashFlow.operating.cashFromCustomers)}</span>
          </div>
          <div className="flex justify-between">
            <span>Cash to Suppliers</span>
            <span className="font-medium text-red-600">{formatCurrency(financialData.cashFlow.operating.cashToSuppliers)}</span>
          </div>
          <div className="flex justify-between">
            <span>Salaries Paid</span>
            <span className="font-medium text-red-600">{formatCurrency(financialData.cashFlow.operating.salariesPaid)}</span>
          </div>
          <div className="flex justify-between">
            <span>Operating Expenses Paid</span>
            <span className="font-medium text-red-600">{formatCurrency(financialData.cashFlow.operating.operatingExpenses)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 mt-2">
            <span className="font-bold">Net Operating Cash Flow</span>
            <span className="font-bold text-green-600">{formatCurrency(financialData.cashFlow.operating.netOperatingCashFlow)}</span>
          </div>
        </div>
      </div>

      {/* Investing Activities */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Investing Activities</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Equipment Purchases</span>
            <span className="font-medium text-red-600">{formatCurrency(financialData.cashFlow.investing.equipmentPurchases)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 mt-2">
            <span className="font-bold">Net Investing Cash Flow</span>
            <span className="font-bold text-red-600">{formatCurrency(financialData.cashFlow.investing.netInvestingCashFlow)}</span>
          </div>
        </div>
      </div>

      {/* Financing Activities */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Financing Activities</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Owners Drawings</span>
            <span className="font-medium text-red-600">{formatCurrency(-8000)}</span>
          </div>
          <div className="flex justify-between">
            <span>Loans Received</span>
            <span className="font-medium text-green-600">{formatCurrency(10000)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 mt-2">
            <span className="font-bold">Net Financing Cash Flow</span>
            <span className="font-bold text-green-600">{formatCurrency(financialData.cashFlow.financing.netFinancingCashFlow)}</span>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-blue-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Cash Flow Summary</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="font-semibold">Net Cash Flow</span>
            <span className="font-bold text-green-600">{formatCurrency(financialData.cashFlow.summary.netCashFlow)}</span>
          </div>
          <div className="flex justify-between">
            <span>Beginning Cash Balance</span>
            <span className="font-medium">{formatCurrency(financialData.cashFlow.summary.beginningCashBalance)}</span>
          </div>
          <div className="flex justify-between border-t-2 pt-2 mt-2">
            <span className="font-bold text-lg">Ending Cash Balance</span>
            <span className="font-bold text-lg text-green-600">{formatCurrency(financialData.cashFlow.summary.endingCashBalance)}</span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderTrialBalance = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold mb-4">Trial Balance</h3>
      <p className="text-sm text-gray-500">As of {formatDate(asOfDate)}</p>

      {/* Trial Balance Table */}
      <div className={`rounded-lg overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-white border'}`}>
        <table className="w-full">
          <thead className={`${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
            <tr>
              <th className="px-4 py-3 text-left">Account Code</th>
              <th className="px-4 py-3 text-left">Account Name</th>
              <th className="px-4 py-3 text-right">Debit</th>
              <th className="px-4 py-3 text-right">Credit</th>
            </tr>
          </thead>
          <tbody>
            {financialData.trialBalance.accounts.map((account, index) => (
              <tr key={index} className={index % 2 === 0 ? (isDark ? 'bg-slate-800' : 'bg-gray-50') : ''}>
                <td className="px-4 py-2">{account.code}</td>
                <td className="px-4 py-2">{account.name}</td>
                <td className="px-4 py-2 text-right">{account.debit > 0 ? formatCurrency(account.debit) : '-'}</td>
                <td className="px-4 py-2 text-right">{account.credit > 0 ? formatCurrency(account.credit) : '-'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className={`${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
            <tr>
              <td className="px-4 py-3 font-bold" colSpan="2">TOTALS</td>
              <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(financialData.trialBalance.totalDebits)}</td>
              <td className="px-4 py-3 text-right font-bold text-red-600">{formatCurrency(financialData.trialBalance.totalCredits)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Balance Verification */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-green-50'}`}>
        <div className="flex items-center justify-center gap-2">
          <span className="text-2xl">{financialData.trialBalance.isBalanced ? '✅' : '❌'}</span>
          <span className="font-bold text-lg">
            {financialData.trialBalance.isBalanced ? 'Trial Balance is Balanced' : 'Trial Balance is Out of Balance'}
          </span>
        </div>
        <div className="text-center text-sm text-gray-500 mt-2">
          Debits ({formatCurrency(financialData.trialBalance.totalDebits)}) = Credits ({formatCurrency(financialData.trialBalance.totalCredits)})
        </div>
      </div>
    </div>
  )

  const renderTaxTransactions = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold mb-4">Tax Transactions</h3>
      <p className="text-sm text-gray-500">For the period ended {formatDate(asOfDate)}</p>

      {/* GST Transactions */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">GST Transactions</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>GST Collected</span>
            <span className="font-medium text-green-600">{formatCurrency(financialData.taxTransactions.gst.collected)}</span>
          </div>
          <div className="flex justify-between">
            <span>GST Paid</span>
            <span className="font-medium text-red-600">{formatCurrency(financialData.taxTransactions.gst.paid)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 mt-2">
            <span className="font-bold">Net GST Payable</span>
            <span className="font-bold text-blue-600">{formatCurrency(financialData.taxTransactions.gst.netPayable)}</span>
          </div>
        </div>
      </div>

      {/* Income Tax */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Income Tax</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Income Tax Provision</span>
            <span className="font-medium">{formatCurrency(financialData.taxTransactions.incomeTax.provision)}</span>
          </div>
          <div className="flex justify-between">
            <span>Tax Payments Made</span>
            <span className="font-medium text-green-600">{formatCurrency(financialData.taxTransactions.incomeTax.payments)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 mt-2">
            <span className="font-bold">Outstanding Income Tax</span>
            <span className="font-bold text-orange-600">{formatCurrency(financialData.taxTransactions.incomeTax.outstanding)}</span>
          </div>
        </div>
      </div>

      {/* Employment Taxes */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Employment Taxes</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Employee EPF</span>
            <span className="font-medium">{formatCurrency(financialData.taxTransactions.employmentTaxes.epf)}</span>
          </div>
          <div className="flex justify-between">
            <span>Employee SOCSO</span>
            <span className="font-medium">{formatCurrency(financialData.taxTransactions.employmentTaxes.socso)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 mt-2">
            <span className="font-bold">Total Employment Taxes</span>
            <span className="font-bold">{formatCurrency(financialData.taxTransactions.employmentTaxes.totalEmploymentTaxes)}</span>
          </div>
        </div>
      </div>

      {/* Tax Summary */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-blue-50'}`}>
        <h4 className="text-lg font-semibold mb-3">Tax Summary</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="font-semibold">Total Tax Payable</span>
            <span className="font-bold">{formatCurrency(financialData.taxTransactions.summary.totalTaxPayable)}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-semibold">Tax Payments Made</span>
            <span className="font-bold text-green-600">{formatCurrency(financialData.taxTransactions.summary.taxPaymentsMade)}</span>
          </div>
          <div className="flex justify-between border-t-2 pt-2 mt-2">
            <span className="font-bold text-lg">Outstanding Tax Balance</span>
            <span className="font-bold text-lg text-orange-600">{formatCurrency(financialData.taxTransactions.summary.outstandingBalance)}</span>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className={`space-y-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold">Financial Statements</h2>
        <input
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
          className={`px-3 py-2 rounded border ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}
        />
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b">
        {['summary', 'balanceSheet', 'incomeStatement', 'cashFlow', 'trialBalance', 'taxTransactions'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-blue-500 text-blue-600'
                : isDark ? 'border-transparent text-gray-400 hover:text-white' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'summary' ? 'Report Summary' :
             tab === 'balanceSheet' ? 'Balance Sheet' :
             tab === 'incomeStatement' ? 'Income Statement' :
             tab === 'cashFlow' ? 'Cash Flow' :
             tab === 'trialBalance' ? 'Trial Balance' :
             'Tax Transactions'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'summary' && renderReportSummary()}
        {activeTab === 'balanceSheet' && renderBalanceSheet()}
        {activeTab === 'incomeStatement' && renderIncomeStatement()}
        {activeTab === 'cashFlow' && renderCashFlow()}
        {activeTab === 'trialBalance' && renderTrialBalance()}
        {activeTab === 'taxTransactions' && renderTaxTransactions()}
      </div>
    </div>
  )
}